import React from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { InstallerLogo, appConfig, installerBranding } from "@/branding/Branding";
import { Mail, User as UserIcon, ShieldCheck, Info, ChevronRight, Building2 } from "lucide-react";
import { PageContainer, PageHeader } from "@/components/layout/PageLayout";
import { DeleteAccountRow } from "@/components/DeleteAccountRow";
import NotificationPreferences from "@/components/notifications/NotificationPreferences";
import SecurityAccountSection from "@/components/SecurityAccountSection";
import { versionLine, copyrightLine } from "@/config/app";

export default function Profile() {
    const { user, logout } = useAuth();
    const navigate = useNavigate();
    if (!user) return null;
    // FIELD-001C · Distinguish account role from resource type on
    // field-user profiles. Account role governs application access;
    // resource type is a separate attribute of the linked field-team
    // record. Missing → "Not specified" (never guess a trade).
    const isFieldUser = user.role === "installer";
    const isOps = user.role === "operations";
    const eyebrow = isFieldUser ? "FIELD USER" : isOps ? "OPERATIONS" : "CUSTOMER";
    const roleChip = isFieldUser ? "Field User" : isOps ? "Operations" : "Homeowner";
    const heading = isFieldUser ? "Your account" : isOps ? "Your profile" : "Your profile";
    const subtitle = (isFieldUser || isOps)
        ? "Your account details."
        : "Account details we use to keep your installation on track.";
    const resourceType = user.engineer_resource_type || user.resource_type || null;
    return (
        <div className="min-h-screen bg-[#F5F5F7] pb-20" data-testid="profile-page">
            <PageContainer width="narrow">
                <PageHeader
                    eyebrow={eyebrow}
                    title={heading}
                    subtitle={subtitle}
                    icon={UserIcon}
                    backFallback={isFieldUser ? "/installer" : isOps ? "/operations" : "/dashboard"}
                    testid="profile"
                />

                <div className="pe-card p-6 md:p-8 mt-6">
                    <div className="flex items-center gap-4">
                        <div className="w-14 h-14 rounded-full bg-[#E6F1DE] text-[#3E9E2E] text-xl font-medium flex items-center justify-center">
                            {user.name.split(" ").map(n => n[0]).slice(0, 2).join("")}
                        </div>
                        <div>
                            <div className="text-xl text-[#131B2F] font-medium">{user.name}</div>
                            <div className="text-sm text-[#4B5565] mt-0.5 flex items-center gap-1.5" data-testid="profile-role-chip">
                                <ShieldCheck size={13} strokeWidth={1.75} /> {roleChip}
                            </div>
                        </div>
                    </div>

                    <dl className="mt-6 space-y-4">
                        <div>
                            <dt className="text-sm text-[#4B5565] flex items-center gap-1.5"><Mail size={12} /> Email</dt>
                            <dd className="text-base text-[#131B2F] font-medium mt-1" data-testid="profile-email">{user.email}</dd>
                        </div>
                        <div>
                            <dt className="text-sm text-[#4B5565]">Account role</dt>
                            <dd className="text-base text-[#131B2F] font-medium mt-1" data-testid="profile-account-role">{roleChip}</dd>
                        </div>
                        {isFieldUser && (
                            <div>
                                <dt className="text-sm text-[#4B5565]">Resource type</dt>
                                <dd className="text-base text-[#131B2F] font-medium mt-1" data-testid="profile-resource-type">
                                    {resourceType || "Not specified"}
                                </dd>
                            </div>
                        )}
                        <div>
                            <dt className="text-sm text-[#4B5565] flex items-center gap-1.5"><Building2 size={12} /> Organisation</dt>
                            <dd className="text-base text-[#131B2F] font-medium mt-1" data-testid="profile-organisation">{installerBranding.name}</dd>
                        </div>
                    </dl>

                    <div className="mt-8 pt-6 border-t border-[#E5E7EB]">
                        <button
                            onClick={() => { logout(); navigate("/"); }}
                            className="pe-btn-outline text-[#E53E3E] hover:bg-[#FEEDE5]"
                            data-testid="profile-signout"
                        >
                            Sign out of {appConfig.name}
                        </button>
                    </div>
                </div>

                {/* About SolarFlow */}
                <Link
                    to="/about"
                    data-testid="profile-about"
                    className="mt-4 pe-card p-4 flex items-center gap-3 hover:border-[#3E9E2E]/25 transition-colors"
                >
                    <span className="w-9 h-9 rounded-full bg-[#3E9E2E]/12 flex items-center justify-center shrink-0">
                        <Info size={18} strokeWidth={1.75} className="text-[#3E9E2E]" />
                    </span>
                    <div className="flex-1 min-w-0">
                        <div className="text-base text-[#131B2F] font-medium">About {appConfig.name}</div>
                        <div className="text-xs text-[#4B5565] mt-0.5">Version, copyright & legal</div>
                    </div>
                    <ChevronRight size={16} className="text-[#4B5565]" />
                </Link>

                {/* Notification preferences */}
                <div className="mt-8">
                    <div className="text-[11px] text-[#4B5565] tracking-[0.14em] font-medium uppercase mb-2 px-1">Notifications</div>
                    <NotificationPreferences />
                </div>

                {/* Security & account — every signed-in user manages their own password + sessions */}
                <SecurityAccountSection testid="profile-security-account" />

                {/* Danger zone */}
                <div className="mt-10">
                    <div className="text-[11px] text-[#4B5565] tracking-[0.14em] font-medium uppercase mb-2 px-1">Danger zone</div>
                    <DeleteAccountRow testid="profile-delete-account" />
                </div>

                {/* Version + copyright — same info the About page shows in full */}
                <div className="mt-8 text-center text-xs text-[#4B5565]" data-testid="profile-version-copyright">
                    {versionLine()} · {copyrightLine()}
                </div>
            </PageContainer>
        </div>
    );
}
